import 'core-js/stable';
import 'regenerator-runtime/runtime';
import { ThemeProvider } from '@2n/design-system';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import InfoIcon from '@mui/icons-material/Info';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import WarningIcon from '@mui/icons-material/Warning';
import { styled } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { LicenseInfo } from '@mui/x-license';
import moment from 'moment';
import { MaterialDesignContent, SnackbarProvider } from 'notistack';
import { useEffect, useState } from 'react';
import * as React from 'react';
import { CookiesProvider } from 'react-cookie';
import { createRoot } from 'react-dom/client';
import { IntlProvider } from 'react-intl';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ScrollToTop, ServiceAlert } from './components';
import { store } from './modules/configureStore';
import { selectors as localizationSelectors } from './modules/localization';
import { setIsFetchingMessages, setIsUserLanguageRerendered } from './modules/localization/actions';
import * as localizationMessages from './modules/localization/messages';
import { addSnackbarSuccess } from './modules/notifications/actions';
import App from './pages/App';
import * as serviceWorker from './serviceWorker';
import { palette, themeApplicationOptions, themeOptions } from './theme';
import { getDefaultRichTextElements, getMessagesByLocale, getMuiLocalizations } from './translations';

LicenseInfo.setLicenseKey(
  'f29ba6daabfaf92113648e39fcb1719eTz0xMjk3NTUsRT0xODEyMDY3MTk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMS0yMDI2LFE9MjEsQVQ9bXVsdGksS1Y9Mg=='
);

function Index({ currentLanguage }: { currentLanguage: string }): React.JSX.Element {
  const [messages, setMessages] = useState<any>(undefined);
  const isLanguageRerendered = useSelector(localizationSelectors.isLanguageRerendered);
  const dispatch = useDispatch();

  const getRouterBasename = () => {
    const revisionName = process.env.REVISION_NAME;
    if (revisionName) {
      return '/' + revisionName;
    }
    return undefined;
  };

  const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
    '&.notistack-MuiContent-info': {
      backgroundColor: palette.notification.info.main,
      color: palette.notification.info.text,
    },
  }));

  useEffect(() => {
    async function getMessagesByCurrentLang() {
      const language = moment.locale();
      const loadedMessages = await getMessagesByLocale(currentLanguage);
      moment.locale(currentLanguage);
      setMessages(loadedMessages);
      dispatch(setIsFetchingMessages(false));
      if (isLanguageRerendered && language !== currentLanguage) {
        dispatch(addSnackbarSuccess(localizationMessages.default.localizationSnackbarLanguageSetSuccessfully));
        dispatch(setIsUserLanguageRerendered(false));
      }
    }

    dispatch(setIsFetchingMessages(true));
    getMessagesByCurrentLang();
  }, [currentLanguage]);

  if (messages === undefined) {
    return <>{false}</>;
  }

  return (
    <IntlProvider
      locale={currentLanguage}
      messages={messages}
      defaultRichTextElements={getDefaultRichTextElements()}
      onWarn={(warning: string) => {
        if (!warning.includes('pre-compiled')) {
          // eslint-disable-next-line no-console
          console.warn(warning);
        }
      }}
    >
      <BrowserRouter basename={getRouterBasename()}>
        <SnackbarProvider
          anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
          iconVariant={{
            error: <ErrorOutlineIcon sx={{ mr: 1 }} />,
            info: <InfoIcon sx={{ mr: 1 }} />,
            success: <TaskAltIcon sx={{ mr: 1 }} />,
            warning: <WarningIcon sx={{ mr: 1 }} />,
          }}
          maxSnack={3}
          Components={{
            info: StyledMaterialDesignContent,
          }}
        >
          <ScrollToTop>
            <ServiceAlert show={false} />
            <App />
          </ScrollToTop>
        </SnackbarProvider>
      </BrowserRouter>
    </IntlProvider>
  );
}

const Providers = () => {
  const currentUserLanguage = useSelector(localizationSelectors.getCurrentUserLanguage);
  const currentLanguage = currentUserLanguage?.replace('_', '-') ?? 'en';
  const muiLocalizations = React.useMemo(() => getMuiLocalizations(currentLanguage), [currentLanguage]);

  return (
    <ThemeProvider
      theme="myToN"
      themeApplicationOptions={[{ ...themeApplicationOptions, ...muiLocalizations }]}
      themeOptions={themeOptions}
    >
      <CssBaseline />
      <Index currentLanguage={currentLanguage} />
    </ThemeProvider>
  );
};

const container = document.getElementById('root');

if (container) {
  const root = createRoot(container);

  root.render(
    <Provider store={store}>
      <CookiesProvider>
        <Providers />
      </CookiesProvider>
    </Provider>
  );
} else {
  // eslint-disable-next-line no-console
  console.error('Root container missing in DOM. Failed to mount React app.');
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
